import { Injectable } from '@angular/core';
import { AppConfig, sleep } from '@tytapp/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UserService } from 'user';
import { UsersApi, ProductionsApi, LiveStreamsApi, ApiVOD, ApiPodcast, ApiLiveStream, ApiProduction, ApiAvAsset } from '@tytapp/api';

import { PlayerComponent } from './player/player.component';
import { PlaybackSession, PlayState, PlaylistType, Playback } from './playback';
import { PlayerService } from './player/player.service';
import { Router } from '@angular/router';
import { LiveStreamsService } from 'common-ui';
import { environment } from '@tytapp/environment';

export const CAST_NAMESPACE_TYT = 'urn:x-cast:com.tyt.cast';

export interface TYTCastMediaLoadData {
    type: string;
}

export interface TYTCastLiveMediaLoadData {
    type: "live";
    liveStreamID: number;
}

export interface TYTCastVODMediaLoadData {
    type: "vod";
    productionID: string;
    playlistID: PlaylistType;
    playlistItemID: string;
}

export interface TYTCastMessage {
    type: string;
}

export interface TYTCastMediaChangedMessage extends TYTCastMessage {
    type: "mediaChanged";
    item: ApiVOD | ApiLiveStream | ApiPodcast;
    production: ApiProduction;
}

export interface TYTCastLoginMessage extends TYTCastMessage {
    type: "login";
    token: string;
}

@Injectable()
export class GoogleCastService {

    castContext: cast.framework.CastContext;

    private _availableChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _connectedChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _connectingChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _stateChanged: Subject<cast.framework.CastStateEventData> = new Subject<cast.framework.CastStateEventData>();

    constructor(
        private appConfig: AppConfig,
        private playerService: PlayerService,
        private playbackService: Playback,
        private userService: UserService,
        private userApi: UsersApi,
        private productionsApi: ProductionsApi,
        private liveStreamsApi: LiveStreamsApi,
        private liveStreamsService: LiveStreamsService,
        private router: Router
    ) {

    }

    public get user() {
        return this.userService.user;
    }

    public get available() {
        return typeof cast !== 'undefined';
    }

    initialize() {
        console.log(`GoogleCast: initializing...`);
        this.castContext = cast.framework.CastContext.getInstance();
        this.castContext.setOptions({
            autoJoinPolicy: chrome.cast.AutoJoinPolicy.TAB_AND_ORIGIN_SCOPED,
            receiverApplicationId: environment.googleCastAppId
        });

        this.castContext.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, async e => {
            if (e.sessionState == cast.framework.SessionState.SESSION_RESUMED || e.sessionState == cast.framework.SessionState.SESSION_STARTED) {
                this.allowResumeOnEnd = false;
                this.playbackService.enableSessionTracking = false;
                this.playbackService.lockControlsVisible = true;

                let session = e.session;
                let resuming = e.sessionState == cast.framework.SessionState.SESSION_RESUMED;

                if (resuming) {
                    setTimeout(async () => {
                        if (!this.currentSession)
                            return;

                        let media = this.currentSession.getMediaSession();
                        if (!media)
                            return;

                        if (!this.playbackService.currentPlayer || !this.playbackService.currentPlayer.getCurrentPlaybackSession()) {

                            // TODO
                            // if (loadRequest.type === 'live') {
                            //     let liveLoad = <TYTCastLiveMediaLoadData> loadRequest;
                            //     let liveStream = await this.liveStreamsService.get(parseInt(''+liveLoad.liveStreamID));

                            //     this.playbackService.appPlayer.playAsset(null, null, [], liveStream, liveStream.asset, 'stream');
                            // } else if (loadRequest.type === 'vod') {
                            //     let vodLoad = <TYTCastVODMediaLoadData> loadRequest;

                            //     let production = await this.productionsApi.get(vodLoad.productionID).toPromise();
                            //     let playlist : any[] = [];

                            //     if (vodLoad.playlistID === 'episodes')
                            //         playlist = production.full_length_vods;
                            //     else if (vodLoad.playlistID === 'clips')
                            //         playlist = production.vod_clips;
                            //     else if (vodLoad.playlistID === 'podcasts')
                            //         playlist = production.full_length_podcasts;

                            //     let item = playlist.find(x => x.id === vodLoad.playlistItemID);

                            //     this.playbackService.appPlayer.playAsset(
                            //         production,
                            //         vodLoad.playlistID, playlist,
                            //         item,
                            //         item.asset,
                            //         vodLoad.playlistID === 'podcasts' ? 'audio' : 'video'
                            //     );
                            // }
                        }
                    }, 1000);
                }
                session.addMessageListener(CAST_NAMESPACE_TYT, async (ns, rawMessage) => {
                    console.log(`GoogleCast: Received event (${ns}):`);
                    let message: TYTCastMessage = JSON.parse(rawMessage);

                    if (message.type === 'mediaChanged') {
                        let mediaChangedMessage = <TYTCastMediaChangedMessage>message;
                        let item = mediaChangedMessage.item;
                        let production = mediaChangedMessage.production;

                        console.log(`GoogleCast: Current media has changed to:`);
                        console.dir({ item, production });

                        this;

                        if (!this.playbackService.isAppPlayerActive && this.playbackService.currentPlayer) {
                            let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();

                            if (session['isCastRemoteSession'] && !session['isCurrentMedia']) {
                                if (item.type === 'live_stream') {
                                    console.log(`Navigating to new live stream page:`);
                                    this.router.navigate([`/live/streams/${item.id}`]);

                                } else if (item.type === 'vod') {
                                    let vod = <ApiVOD>item;
                                    let playlistID = 'episodes';
                                    if (production.vod_clips.some(x => x.id === vod.id))
                                        playlistID = 'clips';

                                    console.log(`Navigating to new media page:`);
                                    this.router.navigate([`/watch/${production.show.slug || production.show.id}/${production.id}/${playlistID}/${vod.id}`]);
                                }
                            }
                        }

                    }

                    console.dir(message);
                });

                if (resuming)
                    console.log(`GoogleCast: Cast session resuming`);
                else
                    console.log(`GoogleCast: Cast session started`);

                if (session.getMediaSession()) {
                    console.log(`GoogleCast: Media is already playing back.`);
                } else {
                    console.log(`GoogleCast: No initial media session.`);
                }

                let autoplayOnRemote = !resuming;

                if (this.playbackService.currentPlayer) {
                    let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();

                    if (session && !session['isGoogleCastSession']) {
                        // Take it over!
                        console.log(`GoogleCast: Switching to GoogleCast remote playback session...`);

                        let newSession = await this.playbackService.currentPlayer.refreshMediaResolution(!resuming);
                        console.log(`GoogleCast: Re-resolved playback session to:`);
                        console.dir(newSession);
                    }
                }

                session.addEventListener(cast.framework.SessionEventType.MEDIA_SESSION, async e => {
                    if (this.playbackService.currentPlayer) {
                        let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();
                        let existingMediaId = undefined;

                        // TODO: this could be causing playstate jitter
                        if (session.item.type === 'stream') {
                            existingMediaId = `live:${session.item.item.id}`;
                        } else {
                            existingMediaId = `vod:${session.item.item.id}`;
                        }

                        if (session['isGoogleCastSession'] && session[''])
                            console.log(`GoogleCast: Refreshing GoogleCast remote playback session for the new media session...`);

                        let refreshAutoplay = autoplayOnRemote;
                        autoplayOnRemote = false;

                        let newSession = await this.playbackService.currentPlayer.refreshMediaResolution(refreshAutoplay);
                    }
                });
            } else if (e.sessionState == cast.framework.SessionState.SESSION_ENDED || e.sessionState == cast.framework.SessionState.SESSION_ENDING) {

                this.playbackService.enableSessionTracking = true;
                this.playbackService.lockControlsVisible = false;
            }
        });

        this.castContext.addEventListener(cast.framework.CastContextEventType.CAST_STATE_CHANGED, async e => {
            console.log(`GoogleCast: State changed: ${e.castState}`);

            if (e.castState == "NO_DEVICES_AVAILABLE") {
                this._availableChanged.next(false);
                this._connectingChanged.next(false);
                this._connectedChanged.next(false);
                this._connected = false;
            } else if (e.castState == "NOT_CONNECTED") {
                this._availableChanged.next(true);
                this._connectingChanged.next(false);
                this._connectedChanged.next(false);
                this._connected = false;
                this.playbackService.globalBuffering = false;
            } else if (e.castState == "CONNECTING") {
                this._availableChanged.next(true);
                this._connectingChanged.next(true);
                this._connectedChanged.next(false);
                this._connected = true;
                this.playbackService.globalBuffering = true;

                let player = this.playbackService.currentPlayer;
                if (player) {
                    let session = player.getCurrentPlaybackSession();
                    if (session)
                        session.pause();
                }

            } else if (e.castState == "CONNECTED") {
                this._availableChanged.next(true);
                this._connectingChanged.next(false);
                this._connectedChanged.next(true);
                this._connected = true;
                this.playbackService.globalBuffering = false;
            }

            this._stateChanged.next(e);

            if (!this._connected && this.playbackService.currentPlayer) {

                // Sleep to allow the cast session to fully end before transitioning back.
                await sleep(1000);

                let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();

                if (session['isGoogleCastSession']) {
                    // Take it back!

                    console.log(`GoogleCast: Switching from GoogleCast playback session back to regular playback...`);

                    let isPlaying = !['paused', 'unstarted'].includes(session.playState);
                    let newSession = await this.playbackService.currentPlayer.refreshMediaResolution(isPlaying && this.allowResumeOnEnd);

                    console.log(`GoogleCast: Re-resolved playback session to:`);
                    console.dir(newSession);
                }
            }
        });
    }

    async sendLoginInfo() {
        try {
            await this.castContext.getCurrentSession().sendMessage(CAST_NAMESPACE_TYT, <TYTCastLoginMessage>{
                type: 'login',
                token: this.userService.token
            });
        } catch (e) {
            console.error(`Chromecast: Caught error while sending login info`);
            console.error(e);
            throw e;
        }
    }

    async loadMedia(asset: ApiAvAsset, position: number, item: ApiVOD | ApiPodcast | ApiLiveStream, production?: ApiProduction) {
        await this.sendLoginInfo();

        let mediaInfo = new chrome.cast.media.MediaInfo(`${asset.provider}:${asset.url}`, 'video/mp4');
        let request = new chrome.cast.media.LoadRequest(mediaInfo);
        request.currentTime = position;

        console.log(`GoogleCast: loadMedia(): Request:`);
        console.dir(request);

        await this.castContext.getCurrentSession().loadMedia(request);

        console.log(`Sending media change event`);
        await this.castContext.getCurrentSession().sendMessage(CAST_NAMESPACE_TYT, {
            type: 'mediaChanged',
            production,
            item
        });
    }

    async castNow() {
        try {
            await this.castContext.requestSession();
        } catch (e) {
            console.log(`Failed to request session:`);
            console.log(e);
        }
    }

    /**
     * Set to true when the user explicitly ends the
     * session. When true, the media will autoplay upon
     * resuming local playback.
     */
    allowResumeOnEnd = false;

    async stopCasting() {
        this.allowResumeOnEnd = true;
        let castSession = cast.framework.CastContext.getInstance().getCurrentSession();
        castSession.endSession(true);
    }

    get currentSession() {
        if (typeof cast === 'undefined')
            return null;
        if (!cast.framework || !cast.framework.CastContext || !cast.framework.CastContext.getInstance)
            return null;

        let context = cast.framework.CastContext.getInstance();

        if (context == null)
            return null;

        return context.getCurrentSession();
    }

    _connected: boolean = false;

    get connected(): boolean {
        return this._connected;
    }

    get stateChanged(): Observable<cast.framework.CastStateEventData> {
        return this._stateChanged;
    }

    get connectingChanged(): Observable<boolean> {
        return this._connectingChanged;
    }

    get availableChanged(): Observable<boolean> {
        return this._availableChanged;
    }

    get connectedChanged(): Observable<boolean> {
        return this._connectedChanged;
    }

    // load on receiver component ngOnInit()

    public get isReceiver() {
        if (typeof window === 'undefined')
            return false;

        return window.location.pathname.includes('/receiver');
    }

    mediaTitle: string = null;
    mediaShowName: string = null;
    mediaThumbnail: string = null;
    mediaIsLive: boolean = false;

    loadingNewMedia: boolean = false;
    _paywalled: boolean = false;

    get paywalled() {
        return this._paywalled;
    }

    set paywalled(value: boolean) {
        if (value && this._paywalled != value) {
            if (this.manager) {
                let receiverManager = cast.receiver.CastReceiverManager.getInstance();

                if (receiverManager) {
                    let caps = receiverManager.getDeviceCapabilities();

                    if (!caps.display_supported) {

                        // TODO: this doesn't seem to work on Cast devices.
                        // The alternative is to have the message recorded

                        if (typeof SpeechSynthesisUtterance !== 'undefined') {
                            let message = '';
                            if (this.user && this.user.membership && !this.user.membership.cancelled_at)
                                message = 'This content requires TYT membership to listen. Please visit tyt.com/account to update your billing information.';
                            else if (this.user)
                                message = 'This content requires TYT membership to listen. Visit tyt.com/join to become a member.';
                            else
                                message = 'This content requires TYT membership to listen. Login or Visit tyt.com/join to become a member';
                            var msg = new SpeechSynthesisUtterance(message);
                            window.speechSynthesis.speak(msg);
                        }
                    }
                }
            }
        }
        this._paywalled = value;
    }
    pendingLogin: Promise<void> = Promise.resolve();
    manager: cast.receiver.MediaManager;

    tytMessageBus: cast.receiver.CastMessageBus;

    initialized: boolean = false;
    initializeSender() {
        if (this.initialized)
            return;

        if (typeof window === 'undefined')
            return;

        if (window.location.pathname === '/cast/receiver') {
            console.warn("Refusing to load Cast Sender framework when running as Receiver.");
            return;
        }

        this.initialized = true;

        // if (this.isReceiver)
        //     return;

        let self = this;
        window['__onGCastApiAvailable'] = (isAvailable) => {
            if (isAvailable) {
                self.initialize();
            } else {
                console.log('(I) Google Cast: Not available');
            }
        };

        let script = document.createElement('script');
        script.src = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
        document.body.appendChild(script);
    }

    broadcastMediaChange(media: TYTCastMediaLoadData) {
        this.manager.broadcastStatus(true);
        this.tytMessageBus.broadcast(<TYTCastMessage>{
            type: 'mediaChanged',
            media
        });
    }
}